import type { Consumer } from '@rails/actioncable'
import { configureApiClient } from '@transreport/pauk-rrms-api'
import type { User } from 'oidc-client-ts'
import { useEffect, useState } from 'react'

import { syncActionCableAuth } from '@/lib/actionCable'
import { useAuthStore } from '@/lib/stores/auth'
import { useStationStore } from '@/lib/stores/station'

type UseApiAuthSyncArgs = {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
}

export function useAuthenticatedClient({ user, isAuthenticated, isLoading }: UseApiAuthSyncArgs) {
  const validUser = user && !user.expired ? user : null
  const token = validUser?.access_token ?? null
  const isHydrationPending = isAuthenticated && !validUser
  const [syncedToken, setSyncedToken] = useState<string | null>(null)
  const [consumer, setConsumer] = useState<Consumer | null>(null)

  useEffect(() => {
    if (isLoading) {
      return
    }

    if (isAuthenticated && !validUser) {
      return
    }

    configureApiClient({
      getHeaders: () =>
        token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>),
    })
    const newConsumer = syncActionCableAuth(token)
    setConsumer(newConsumer)

    if (validUser) {
      useAuthStore.getState().setAuth(validUser)
    } else {
      useAuthStore.getState().clearAuth()
      useStationStore.getState().clearSelectedStationCode()
    }

    setSyncedToken(token)
  }, [isAuthenticated, isLoading, token, validUser])

  return {
    token,
    isSynced: !isHydrationPending && syncedToken === token,
    consumer,
  }
}
